import React, { useState } from 'react';
import classnames from 'classnames';
import {
  Divider,
  Text,
  IconButton,
  Tag,
  Button,
  TextButton,
  Tooltip,
  Input,
} from 'wix-style-react';
import { Star, StarFilled, Edit, ChevronRightLarge } from 'wix-ui-icons-common';
import { TranslationFunction } from 'react-i18next';

import { Revision as RevisionType } from '../../types';

import styles from './Revision.scss';

export interface ExtendedRevisionProps {
  t: TranslationFunction;
  isActive: boolean;
  isCurrent: boolean;
  isSeparateRevisions: boolean;
  value: RevisionType;
  onView(revision: RevisionType): void;
  onRestore(revision: RevisionType): void;
  onRename(revision: RevisionType, newTitle: string): void;
  onStar(revision: RevisionType, newFlag: boolean): void;
}

export const Revision: React.FC<ExtendedRevisionProps> = ({
  t,
  isActive,
  isCurrent,
  isSeparateRevisions,
  value,
  onView,
  onRestore,
  onRename,
  onStar,
}) => {
  const getInitialTitleValue = () =>
    value.comment ||
    new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }).format(new Date(value.dateCreated));
  const [isFlagged, setIsFlagged] = useState(Boolean(value.isStarred));
  const [isEditMode, setEditMode] = useState(false);
  const [localTitle, setLocalTitle] = useState(getInitialTitleValue);
  const [prevLocalTitle, setPrevLocalTitle] = useState(getInitialTitleValue);

  const identity = () => {
    if (
      value.creatorRole === 'CONTRIBUTOR' ||
      value.creatorRole === 'UNKNOWN'
    ) {
      return value.email;
    }

    if (value.creatorRole === 'WIX_STAFF') {
      return t('WIX_STAFF_CREATOR_ROLE');
    }

    if (value.creatorRole === 'OWNER') {
      return t('OWNER_CREATOR_ROLE');
    }

    return '';
  };

  const handleFlaggedClick = () => {
    const activeFlag = !isFlagged;
    onStar(value, activeFlag);
    setIsFlagged(activeFlag);
  };

  const handleTitleClick = () => {
    setEditMode(true);
  };

  const handleTitleChange = (e: any) => {
    setLocalTitle(e.target.value);
  };

  const handleTitleInputBlur = () => {
    if (prevLocalTitle !== localTitle) {
      onRename(value, localTitle);
      setPrevLocalTitle(localTitle);
    }
    setEditMode(false);
  };

  const handleViewClick = () => {
    onView(value);
  };

  const handleRestoreClick = () => {
    onRestore(value);
  };

  return (
    <div
      className={classnames(styles.revisionRoot, {
        [styles.activeRevision]: isActive,
      })}
      data-hook="revision"
    >
      <div className={styles.iconCol}>
        <IconButton
          as="button"
          onClick={handleFlaggedClick}
          skin="inverted"
          priority="secondary"
          size="small"
          dataHook="revision-star"
        >
          {isFlagged ? <StarFilled /> : <Star />}
        </IconButton>
      </div>
      <div className={styles.contentCol}>
        {isSeparateRevisions && value.schemaVersion >= 2 && (
          <div className={styles.tags}>
            {value.editorType === 'adi' && (
              <Tag
                id={`${value.revision}-${value.editorType}`}
                size="tiny"
                theme="neutral"
                removable={false}
              >
                WIX ADI
              </Tag>
            )}
            {value.editorType === 'editor' && (
              <Tag
                id={`${value.revision}-${value.editorType}`}
                size="tiny"
                theme="standard"
                removable={false}
              >
                WIX EDITOR
              </Tag>
            )}
          </div>
        )}
        <div className={styles.title}>
          {!isEditMode && (
            <>
              <Text ellipsis data-hook="revision-title">
                {localTitle}
              </Text>
              <span className={styles.editBtn}>
                <Tooltip content={t('RENAME')} fixed textAlign="center">
                  <TextButton
                    dataHook="revision-edit"
                    as="button"
                    onClick={handleTitleClick}
                    skin="dark"
                    suffixIcon={<Edit />}
                    weight="normal"
                  />
                </Tooltip>
              </span>
            </>
          )}
          {isEditMode && (
            <Input
              className={styles.input}
              dataHook="revision-title-editable-input"
              value={localTitle}
              autoFocus
              maxLength={40}
              size="small"
              onChange={handleTitleChange}
              onBlur={handleTitleInputBlur}
              onEnterPressed={handleTitleInputBlur}
            />
          )}
        </div>
        <div>
          <Text size="tiny" skin="standard" tagName="span" weight="thin">
            {value.isPublished && `${t('PUBLISHED')} `}
            {value.isReleaseCandidate && `${t('RELEASE_CANDIDATE')} `}
            {!value.autosaved &&
              !value.isPublished &&
              !value.isReleaseCandidate &&
              `${t('MANUALLY_SAVED_REVISION')} `}
            {!value.autosaved &&
              !value.isPublished &&
              !value.isReleaseCandidate && (
                <Divider direction="vertical" skin="dark" />
              )}
            {identity()}
          </Text>
        </div>
      </div>
      <div className={styles.actionCol}>
        {(!isActive || isCurrent) && (
          <Tooltip content={t('VIEW_TOOLTIP')} fixed textAlign="center">
            <IconButton
              dataHook="revision-select"
              as="button"
              size="small"
              disabled={isCurrent && isActive}
              priority="secondary"
              onClick={handleViewClick}
            >
              <ChevronRightLarge />
            </IconButton>
          </Tooltip>
        )}
        {isActive && !isCurrent && (
          <Button
            as="button"
            onClick={handleRestoreClick}
            size="small"
            dataHook="revision-restore"
          >
            {t('RESTORE')}
          </Button>
        )}
      </div>
    </div>
  );
};
